import { useCallback, useState } from 'react';
import type { SettingsPageDataAndHandlers } from '../context';
import { useProjectSettingsContext } from '../context';
import { selectOptionFactory } from '../../../../utils';
import type { UpdateHookType } from '../../../../../shared/model-typings';
import type { ProjectUpdateHook, ProjectUpdateHooksSettings } from './types';
import type { SelectOption } from '../../../../types';
import { isCustomAndGithubLocator } from '../../../../../shared/utils';
import type { VCSHost } from '../../../../../shared/projectsV2/vcs';

export const hookTypeToLabelMap: Record<UpdateHookType, string> = {
  none: 'No Automatic Updates',
  scheduled: 'Scheduled',
  github: 'GitHub',
  gitlab: 'GitLab',
  bitbucket_cloud: 'Bitbucket Cloud',
  azure_repos: 'Azure Repos',
  circleci: 'CircleCI',
  travisci: 'TravisCI',
  webhook: 'Webhook',
};

//  This helps us filter out options from this list which don't match
//  this project's version control host.
const nonVcsHookTypes = new Set<UpdateHookType>([
  'none',
  'scheduled',
  'circleci',
  'travisci',
  'webhook',
]);

const getIsHybridGitHubAppProject = (
  state: SettingsPageDataAndHandlers<ProjectUpdateHooksSettings>,
  vcsHost: VCSHost,
) => {
  return isCustomAndGithubLocator(state.locator) && vcsHost === 'github-app';
};

/**
 * This function determines if the project is a provided project.
 */
const getIsProvidedProject = (state: SettingsPageDataAndHandlers<ProjectUpdateHooksSettings>, vcsHost: VCSHost) => {
  return !getIsHybridGitHubAppProject(state, vcsHost) && state.settings.isProvidedProject;
};

/**
 * We filter the list of available options depending on the type of project and the VCS host.
 * For example, it doesn't make sense to show the GitHub option if the project is a BitBucket project.
 */
const getHookTypeOptions = (
  state: SettingsPageDataAndHandlers<ProjectUpdateHooksSettings>,
  vcsHost: VCSHost,
): Array<SelectOption<UpdateHookType>> => {
  const { settings } = state;
  const allHookTypeOptions = Object.entries(hookTypeToLabelMap)
    .map(([value, label]) => selectOptionFactory<UpdateHookType>(value as UpdateHookType, label));

  //  Nothing is displayed if the project is a provided project anyway,
  //  but we may as well just return an empty array.
  if (getIsProvidedProject(state, vcsHost)) {
    return [];
  }

  //  SBOM/Archive uploaded projects cannot get updates from version control hosts, so we filter them out.
  if (settings.projectType === 'sbom' || settings.projectType === 'archive') {
    return allHookTypeOptions.filter(({ value }) => nonVcsHookTypes.has(value));
  }

  //  If the project has a version control host, we show all the nonVcsHookTypes
  //  and the hook type for the specific VCS host.
  if (settings.vcsHost) {
    return allHookTypeOptions.filter(({ value }) => {
      //  If the hook type is not a VCS hook type, we show it.
      if (nonVcsHookTypes.has(value as UpdateHookType)) {
        return true;
      }
      //  If the hook type is the same as the VCS host, we show it.
      if (value === settings.vcsHost) {
        return true;
      }
      //  Since there is no "bitbucket_server" hook type, we show the "bitbucket_cloud" hook type if the VCS host is "bitbucket_server".
      if (settings.vcsHost === 'bitbucket_server' && value === 'bitbucket_cloud') {
        return true;
      }
      if (settings.vcsHost === 'github-app' && value === 'github') {
        return true;
      }
      // nonVcsHookTypes.has(value as UpdateHookType) || value === settings.vcsHost
      return false;
    });
  }

  return allHookTypeOptions;
};

export const defaultScheduledUpdateHook: Pick<ProjectUpdateHook, 'scheduled_interval' | 'scheduled_interval_length' | 'scheduled_interval_time'> = {
  scheduled_interval: 'day',
  scheduled_interval_length: 1,
  scheduled_interval_time: '24:00:00',
};

const defaultUpdateHook = {
  type: null,
  secret_key: '',
  active: true,
};

/**
 * This is the hook for the update hooks settings page.
 * It pulls in the editable fields from the project context,
 * along with helpers for updating and saving the data.
 *
 * This page (unlike the other settings pages) does not depend on
 * any other data beyond the project data.
 */
const useProjectUpdateHooksSettings = () => {
  const { updateHooks: state, project: { vcs_host: vcsHost } } = useProjectSettingsContext();
  const [hookType, setHookType] = useState<UpdateHookType | null>(
    (state.settings.updateHook?.type as UpdateHookType) ?? null,
  );
  const onUpdateHookType = useCallback((nextHookType: UpdateHookType | null) => {
    //  Helper function to create the base hook structure
    const createBaseUpdateHook = (type: UpdateHookType | null) => type === null
      ? null
      : {
          ...defaultUpdateHook,
          ...(state.settings?.updateHook ?? {}),
          type,
        };

    //  Helper function to add scheduling properties if needed.
    const addSchedulingToHook = (hook: ProjectUpdateHook | null) => nextHookType !== 'scheduled' || hook === null
      ? hook
      : {
          ...hook,
          ...defaultScheduledUpdateHook,
        };

    // Use the helper functions to construct the final hook
    const baseHook = createBaseUpdateHook(nextHookType);
    const finalHook = addSchedulingToHook(baseHook as ProjectUpdateHook | null);

    // Update the state and call the update function
    setHookType(nextHookType);
    state.onUpdate({ updateHook: finalHook as ProjectUpdateHook | null });
  }, [state]);

  const isProvidedProject = getIsProvidedProject(state, vcsHost);

  return {
    locator: state.locator,
    isSaving: state.isSaving,
    isDirty: state.isDirty,
    canUserEditSettings: state.canUserEditSettings,
    projectSettingsBaseURL: state.projectSettingsBaseURL,
    //  The save button is always disabled if the project is a provided project.
    isSaveButtonDisabled: isProvidedProject ||
      !state.isDirty ||
      state.isSaving,
    onSave: state.onSave,
    onUpdate: state.onUpdate,
    onUpdateHookType,
    hookType,
    setHookType,
    hookTypeOptions: getHookTypeOptions(state, vcsHost),
    isProvidedProject,
    isSBOMProject: state.settings.projectType === 'sbom',
    updateHook: state.settings.updateHook,
    vcsHost,
  };
};

export default useProjectUpdateHooksSettings;
